/**
 * Centralised help text for the connection form fields, surfaced via
 * `LabelWithHelp` tooltips. Kept in one place so the wording (especially
 * the classification definitions, which are compliance-relevant) is
 * consistent and reviewable. The classification text mirrors the backend
 * `Classification` enum (public / confidential / pii / phi) and the
 * data-classification compliance doc.
 */

export const CONNECTION_HELP = {
  sourceType:
    "The external system this connection points at. Veeva Vault = read documents/metadata via VQL. " +
    "Azure Blob Storage = list / upload / download blobs. " +
    "Azure AI Foundry = chat models for the AI workspace. " +
    "AWS S3 = list / upload / download objects. " +
    "Database = read tables from PostgreSQL, SQL Server, Oracle or MySQL (read-only). " +
    "Generis CARA = read-only search over a CARA tenant's records.",

  classification:
    "How sensitive the data behind this source is — pick the HIGHEST it can contain. " +
    "Public: non-sensitive. Confidential: internal/business-sensitive but not personal (a safe default for most document sources). " +
    "PII: personal data — names, emails, identifiers (GDPR). PHI: protected health information — patient data (HIPAA). " +
    "Drives encryption-key selection, retention, and DSAR handling.",

  authModeVeeva:
    "How DAQUMA authenticates to Veeva. Service-account password (recommended): a Veeva integration user's " +
    "username + password — DAQUMA logs in to get a session. OAuth2 access token: paste a pre-issued Veeva OAuth2 bearer token.",

  authModeBlob:
    "How DAQUMA authenticates to the storage account. Connection string / SAS: paste a full Azure Storage " +
    "connection string (Access keys → Connection string in the portal) or a SAS-based one.",

  authModeS3:
    "How DAQUMA authenticates to AWS, strongest first. OIDC federation (recommended): DAQUMA presents a token " +
    "minted from its own managed identity and exchanges it for short-lived AWS credentials — no secret is stored " +
    "anywhere, but you must register DAQUMA as an OIDC provider in your AWS account. Assume role + external ID: " +
    "DAQUMA assumes a role you own, using a base key that should be permitted to do nothing else; credentials are " +
    "short-lived. Access key: a long-lived key pair — simplest to set up and the weakest of the three.",

  s3Region:
    "The AWS region the bucket lives in, e.g. eu-west-1. Required — S3 will not answer a request signed for the " +
    "wrong region. Non-EU regions are allowed; the region is recorded on the connection and in every access audit " +
    "event so the data's location is always evidenced (GDPR).",

  s3RoleArn:
    "The ARN of the IAM role DAQUMA assumes in your AWS account, e.g. arn:aws:iam::123456789012:role/daquma-reader. " +
    "Grant it only the S3 permissions this connection needs — least privilege is enforced by your IAM policy, not by DAQUMA.",

  s3ExternalId:
    "A shared string that must also appear as an sts:ExternalId condition in the role's trust policy. " +
    "Mandatory: it is what stops a third party from tricking DAQUMA into assuming your role on their behalf " +
    "(the confused-deputy problem).",

  s3AccessKeyId:
    "The AWS access key ID (starts with AKIA). Paired with the secret access key below; the two are stored " +
    "together as one Key Vault secret.",

  vaultUrl:
    "Your Veeva tenant base URL, e.g. https://your-tenant.veevavault.com (no trailing path).",

  username: "The Veeva service-account (integration) user name used with password authentication.",

  apiVersion:
    "Veeva REST API version, e.g. v26.1. Leave the default (the latest we target) unless your vault requires a specific version.",

  caraBaseUrl:
    "Your CARA tenant's base URL, including the application path — e.g. https://your-tenant.cara.cloud/cara. " +
    "The path matters: without it CARA answers 404 or an HTML login page rather than its API. HTTPS only.",

  aiEndpoint:
    "Azure portal → your Azure AI Foundry / Azure OpenAI resource → Keys and Endpoint → copy the endpoint. " +
    "Any of the forms Azure shows works — https://<resource>.openai.azure.com, or the same URL ending in " +
    "/openai or /openai/v1; they all name the same resource and the extra path is trimmed for you. " +
    "The endpoint is an address, not a secret: having it does not grant access.",

  aiDeployments:
    "The models to offer in chats. Discover models asks the endpoint what is actually deployed — using the " +
    "same endpoint and key a chat uses — so a chat can never point at a model that does not exist, and " +
    "each entry carries the version the resource reported. Tick the ones you want available.",

  databaseEngine:
    "Which database product is behind the host. Azure SQL is SQL Server; Azure Database for " +
    "PostgreSQL is PostgreSQL; AWS RDS is whichever engine was provisioned — so pick the engine, " +
    "not the cloud. Oracle addresses a service name instead of a database name.",

  databaseHost:
    "The server's host name and port, e.g. pv-legacy.database.windows.net. The platform reaches it " +
    "outbound over the network — if it sits behind a firewall or a private endpoint, that route has " +
    "to exist before the connection can be tested.",

  databaseAccount:
    "Use a dedicated account with SELECT and nothing else. The platform has no code path that " +
    "writes to a source database, but a read-only account moves the guarantee to your side of the " +
    "line as well — and is positive evidence this tool was not the cause if anything ever changes.",

  databaseTls:
    "Require an encrypted connection. On for anything reachable over a network; the only reason to " +
    "turn it off is a local database that does not offer TLS at all. Supported for PostgreSQL and " +
    "MySQL; SQL Server negotiates encryption with the server, and for Oracle it is a property of " +
    "the listener endpoint.",

  credential:
    "Stored in Azure Key Vault and never shown again after saving (responses only report whether a credential is set). " +
    "Password mode: the service-account password. OAuth2: the access token. Blob: the connection string / SAS. " +
    "Azure AI Foundry: the resource key from Keys and Endpoint. " +
    "S3: the secret access key, stored alongside the key ID. " +
    "Database: the read-only account's password. " +
    "CARA: the API token from your CARA administrator — it is exchanged for a session, never sent as a bearer header.",
} as const;
